/**
 * The waiting room: wishlist-first spending, and the cooldown that guards it.
 *
 * Orbits give money somewhere to go; this is what stops it leaving
 * (CODING_PLAN §15, "the waiting room — the dollar-store fix"). A child cannot
 * file a spend request for something that isn't on their list with a price on
 * it, and anything over a threshold sits for a while before anyone can say yes.
 * "Let's go to the dollar store" becomes "add it to your list" — which is a
 * yes, and which almost always ends the conversation.
 *
 * Nine decisions are load-bearing:
 *
 * 1. **The cooldown is a rule of the ship, not a parent saying no.** Nothing in
 *    this module takes an actor role: the same `askForWish` and the same
 *    `isReady` gate a parent's own proposal against the family envelope. That
 *    is the entire point. A wait that applies to the child and not to the adult
 *    who wrote the rule is a punishment, and a child reads it as one; a wait
 *    that applies to everyone is just how buying works here.
 *
 * 2. **The wait is a function of the amount, priced at ask time and
 *    snapshotted.** `waitDaysFor` is a pure lookup over ascending tiers, and
 *    `askForWish` writes `{ waitDays, ruleVersion }` onto the wish. Same rule as
 *    every ledger entry in this codebase: a parent who lengthens the cooldown
 *    this afternoon has changed the *next* wish, not the one their child filed
 *    this morning. Recomputing `readyOn` from the live rule at decision time
 *    would let a rule change move a finish line a child is already running at,
 *    which teaches that the process is arbitrary — the exact opposite of the
 *    lesson.
 *
 * 3. **`readyOn` is stored on the wish, not derived at read time.** Everything
 *    else in this codebase derives periods rather than storing cursors, and this
 *    is the deliberate exception: a period index is a function of the calendar,
 *    but a cooldown is a function of *when a particular child asked*, which is
 *    an event and has to be recorded. Storing it is also what makes the wait
 *    legible — the child can see the date, and a date you can see is a date you
 *    can wait for.
 *
 * 4. **Dates are `YYYY-MM-DD` and compared as strings.** No timestamps, for the
 *    reason `periods.js` gives: a boundary that depends on the hour is a
 *    boundary that moves when the clocks change, and a family that agrees on
 *    what day it is should agree on whether the wait is over. Every date coming
 *    in is normalised through `toDateOnly` so a `Date` or an ISO timestamp can't
 *    poison a string comparison, and `readyOn` is computed in whole days via
 *    `dayNumber`/`dateFromDayNumber` so month ends and leap days are the
 *    calendar's problem and not ours.
 *
 * 5. **A waiver is loud or it doesn't happen.** `waiveWait` throws without a
 *    reason and records who granted it and when. A parent can always skip a
 *    cooldown — refusing them that would just get the rule switched off — but a
 *    silent waiver is the same corrosion as a fudged Fuel gauge: the number on
 *    the screen stops describing anything. The waiver stays on the wish and in
 *    history, next to the wait it cancelled.
 *
 * 6. **Buying twice cannot pay twice.** `purchaseEntry` keys the settlement on
 *    the wish id (`settlement:<childId>:<wishId>`), so a double-tapped Bought
 *    It, or a retried request whose response was lost, settles once. Same
 *    mechanism as approving a spend proposal in `envelope.js`.
 *
 * 7. **Saving for a thing is a state, not a note.** `startSaving` moves a wish
 *    to `saving` and points it at the Save bucket; `savingProgress` measures it
 *    against a balance passed in and stores nothing. A child can ask from
 *    `saving` before they have the full amount — the parent can front it, and
 *    the screen says which — because refusing to let them ask would make the
 *    honest state (saving, short by $4) worse to be in than the vague one
 *    (never said anything), and would teach that naming a plan costs you
 *    options.
 *
 * 8. **The toy day moves a ready date later and never earlier.** A once-a-month
 *    shopping trip is a scheduling fact, not a permission: `askForWish` takes
 *    the later of the cooldown date and the next toy day, so a trip can never
 *    cancel a wait a child is already counting down. It applies to `toy` and
 *    nothing else — a build kit or an ice-skating afternoon has no reason to
 *    wait for the toy aisle.
 *
 * 9. **The toy limit is a ceiling on money already spent, and going over is
 *    loud.** `toySpentInMonth` counts only `bought` toy wishes for that child
 *    decided in that month, so wanting things costs nothing against the limit
 *    and a sibling's purchase cannot eat it. `decideWish` throws on an approval
 *    that would exceed it unless a reason is given, which is recorded on
 *    `capWaiverReason` — the same rule as `waiveWait`, because a limit that
 *    bends without saying so is not a limit, it is a mood.
 *
 * And the stat that does the teaching: `waitOutcomes` is the child's own
 * evidence about their own impulses — "you waited on 11 things, you still
 * wanted 5, the other 6 were $34 you kept." It counts only wishes that actually
 * entered the room, and `keptCents` counts only the ones the child let go of
 * themselves. See the note on `waitOutcomes` for why a parent's no is not
 * counted as the child's restraint.
 *
 * The vocabulary here is canonical and display-only themes cannot change it, or
 * any value it produces (CLAUDE.md, invariants).
 */

import { dateFromDayNumber, dayNumber, parseDateOnly, toDateOnly } from './periods.js';
import { settlementEntry } from './ledger.js';

export const COOLDOWN_RULE_VERSION = 'fortunaut-cooldown-v1';

/**
 * Tiers are ascending by `overCents`; the wait is the last tier whose
 * `overCents` is at or below the amount.
 *
 * Four tiers, not a formula. A curve would be smoother and no child could
 * predict it; "under five dollars you can just buy it, over fifty you sleep on
 * it for a week" is a rule a nine-year-old can hold in their head, and a rule
 * they can hold in their head is one they start applying before they open the
 * app. The bottom tier is 0/0 on purpose — it makes "no wait" a tier rather
 * than a special case, so the lookup below never has to handle an empty result.
 */
export const COOLDOWN_DEFAULTS = Object.freeze({
  version: COOLDOWN_RULE_VERSION,
  tiers: Object.freeze([
    { overCents: 0, waitDays: 0 },
    { overCents: 500, waitDays: 1 },
    { overCents: 2000, waitDays: 3 },
    { overCents: 5000, waitDays: 7 },
  ]),
});

/**
 * `wished` is on the list and not asked for. `waiting` is in the room. The
 * three terminal states are what `waitOutcomes` reads.
 *
 * `let_go` and `declined` are deliberately separate states rather than one
 * "didn't buy it": the child changing their mind and a parent saying no are
 * different events that teach different things, and collapsing them would let a
 * run of parental noes read back to the child as their own self-control.
 */
export const WISH_STATUSES = Object.freeze([
  'wished',
  'saving',
  'waiting',
  'bought',
  'let_go',
  'declined',
]);

/**
 * What kind of thing this is. Display-only in the sense that no financial value
 * depends on it — with exactly one exception, `toy`, which the monthly toy limit
 * below is scoped to.
 *
 * The list exists because "spending" is not one behaviour. A $40 build kit that
 * arrives monthly, a $40 ice-skating afternoon, a $40 scooter that lasts three
 * years and a $40 pile of dollar-store plastic are the same number and four
 * different decisions, and a family that wants to slow down the fourth almost
 * never wants to slow down the first three. Kinds are what make a limit
 * targetable without making it a blanket "you spend too much", which is the
 * version a child hears as a judgement rather than a rule.
 *
 * `gift` is for someone else and is deliberately its own kind rather than a
 * bucket concern: a child buying their sister a birthday present is not the
 * behaviour any limit here is trying to shape.
 */
export const WISH_KINDS = Object.freeze(['kit', 'experience', 'gear', 'toy', 'gift', 'other']);

export const SPENDING_RULE_VERSION = 'fortunaut-spending-v1';

/**
 * The two shapes of "not right now" that aren't a cooldown.
 *
 * `toyCapCents` is a ceiling on how much of a child's **own** money goes to
 * `toy` wishes in a calendar month. `toyDay` is the once-a-month shopping trip:
 * a day of the month on which toys get bought, so a toy wish's ready date lands
 * on the trip rather than on a random Tuesday. Either can be null and both are
 * null by default — a family that has not met the problem should not meet the
 * machinery, the same call as seconding in `envelope.js`.
 *
 * They are separate on purpose. A cap is about how much, a toy day is about
 * when, and a family usually wants one of them. Together they say "toys are a
 * thing we do on the 15th, up to $20" — which is a rule a child can repeat, and
 * a rule a child can repeat is one they start planning around instead of
 * arguing with.
 *
 * `toyDay` is capped at 28 rather than 31 because a limit that silently cannot
 * happen in February is worse than one that is switched off: see
 * `normaliseToyDay`.
 */
export const SPENDING_DEFAULTS = Object.freeze({
  version: SPENDING_RULE_VERSION,
  toyCapCents: null,
  toyDay: null,
});

/**
 * A toy day only means something if it exists in every month.
 *
 * 29, 30 and 31 are rejected to null rather than clamped: a rule set to the 31st
 * would give a child a ready date that never arrives in February, and a
 * countdown that cannot finish is the one failure this whole module exists to
 * avoid. Rejecting to null switches the feature off visibly (the parent set a
 * day and the app is not showing one) instead of quietly stranding a wish.
 */
function normaliseToyDay(day) {
  const n = Number(day);
  if (!Number.isInteger(n) || n < 1 || n > 28) return null;
  return n;
}

/**
 * A cap of 0 is a real setting — "no toys out of your own money this month" —
 * so only a non-integer or a negative number falls back to "no cap". Note the
 * fallback direction is *off*, the opposite of `withCooldownDefaults`: a
 * malformed cooldown that means "nothing ever waits" removes a guard, whereas a
 * malformed cap that means "nothing may ever be bought" invents one. Both
 * failures are read as the app being broken; only one of them takes money.
 */
export function withSpendingDefaults(rule = {}) {
  const merged = { ...SPENDING_DEFAULTS, ...rule };
  const cap = Number(merged.toyCapCents);
  merged.toyCapCents =
    merged.toyCapCents === null || merged.toyCapCents === undefined || !Number.isInteger(cap) || cap < 0
      ? null
      : cap;
  merged.toyDay = normaliseToyDay(merged.toyDay);
  return merged;
}

/**
 * A rule that arrived without tiers — a partial patch from a settings form, a
 * row written before the column existed — falls back to the defaults rather
 * than to no tiers at all. An empty tier list would mean "nothing ever waits",
 * which is the one failure direction that silently removes the feature.
 */
export function withCooldownDefaults(rule = {}) {
  const merged = { ...COOLDOWN_DEFAULTS, ...rule };
  if (!Array.isArray(merged.tiers) || merged.tiers.length === 0) {
    merged.tiers = COOLDOWN_DEFAULTS.tiers;
  }
  return merged;
}

/**
 * How long this amount waits, in whole days.
 *
 * The tiers are sorted here rather than trusted, because they arrive from a
 * database row a parent edited and an out-of-order list would silently price a
 * $60 wish at a day. Ties on `overCents` keep the longer wait, which is the
 * safe direction.
 */
export function waitDaysFor(amountCents, rule = COOLDOWN_DEFAULTS) {
  const cfg = withCooldownDefaults(rule);
  const amount = Number(amountCents) || 0;

  const tiers = [...cfg.tiers].sort((a, b) => (Number(a.overCents) || 0) - (Number(b.overCents) || 0));
  let days = 0;
  for (const tier of tiers) {
    if ((Number(tier.overCents) || 0) > amount) break;
    days = Math.max(0, Math.trunc(Number(tier.waitDays) || 0));
  }
  return days;
}

/** The date the wait is over: `askedOn` plus the tier's days, in calendar days. */
export function readyOn(askedOn, amountCents, rule = COOLDOWN_DEFAULTS) {
  return dateFromDayNumber(dayNumber(askedOn) + waitDaysFor(amountCents, rule));
}

/**
 * Is this wish decidable today?
 *
 * `>=`, not `>`: a wish is ready **on** its ready date, not the day after. A
 * one-day cooldown that a child cannot clear tomorrow is a two-day cooldown
 * wearing the wrong label, and the number on the screen would be a lie by one
 * sleep — small, constant, and exactly the kind of thing that stops a child
 * believing the countdown.
 */
export function isReady(wish, today) {
  if (!wish || wish.status !== 'waiting') return false;
  if (wish.waivedAt) return true;
  if (!wish.readyOn) return false;
  return toDateOnly(today) >= toDateOnly(wish.readyOn);
}

function assertStatus(wish, expected) {
  const allowed = Array.isArray(expected) ? expected : [expected];
  if (!allowed.includes(wish?.status)) {
    throw new Error(
      `cannot transition wish ${wish?.id}: expected status ${allowed.map((s) => `"${s}"`).join(' or ')}, got "${wish?.status}"`,
    );
  }
}

/**
 * The child asks. This is the only way into the waiting room.
 *
 * It moves no money and commits nothing — asking is free, and the impact
 * preview beside it never editorialises (CLAUDE.md). All it does is start the
 * clock and write down what the clock was set to.
 */
export function askForWish(wish, { askedOn, rule = COOLDOWN_DEFAULTS, spending = SPENDING_DEFAULTS } = {}) {
  assertStatus(wish, ['wished', 'saving']);
  if (!askedOn) throw new Error('asking for a wish needs the date it was asked on');

  const cfg = withCooldownDefaults(rule);
  const spend = withSpendingDefaults(spending);
  const waitDays = waitDaysFor(wish.amountCents, cfg);
  const kind = wish.kind ?? null;
  const on = toDateOnly(askedOn);

  const cooldownReady = readyOn(on, wish.amountCents, cfg);
  const trip = kind === 'toy' ? nextToyDay(on, spend.toyDay) : null;

  return {
    ...wish,
    status: 'waiting',
    askedOn: on,
    // The later of the two, always. A toy day can push a ready date out; it can
    // never pull one in, because that would let a shopping trip cancel a
    // cooldown a child is already counting down.
    readyOn: trip && trip > cooldownReady ? trip : cooldownReady,
    snapshot: {
      ...(wish.snapshot ?? {}),
      waitDays,
      ruleVersion: cfg.version,
      // The rule as it stood, snapshotted like every other rule in this
      // codebase. `kind` is what tells a later reader whether the toy day
      // actually applied to this wish or was simply in force at the time.
      toyDay: spend.toyDay,
      kind,
    },
  };
}

/**
 * The child takes it off the list — from the list itself, or mid-wait.
 *
 * The whole point of the feature. A wish let go while waiting is the row that
 * becomes "money you kept", so this transition has to be one tap and has to be
 * available from inside the room, not only before entering it.
 */
export function letGo(wish, { on } = {}) {
  assertStatus(wish, ['wished', 'waiting']);
  return { ...wish, status: 'let_go', decidedOn: on ? toDateOnly(on) : null };
}

/**
 * A parent skips the wait — visibly, with a reason, or not at all.
 *
 * The wish stays `waiting`: the waiver makes it *ready*, it does not decide it.
 * Collapsing the two would mean skipping a cooldown silently approved the
 * purchase, and a parent who waives at breakfast is often still going to say no
 * at the till.
 */
export function waiveWait(wish, { actorId, reason, on } = {}) {
  assertStatus(wish, 'waiting');
  if (!reason) throw new Error('a waived cooldown needs a visible reason');
  return {
    ...wish,
    waivedBy: actorId ?? null,
    waivedAt: on ? toDateOnly(on) : null,
    waiverReason: reason,
  };
}

/**
 * Yes or no, once the wait is over.
 *
 * `today` defaults to the decision date, because a decision recorded as
 * happening on a day was made on that day; passing them separately exists only
 * for a back-dated correction, and even then approving early still throws.
 * Declining is never gated on readiness — making a child sit out a week before
 * they can be told no is pure friction, the same reason a decline needs no
 * sibling's second in `envelope.js`.
 */
export function decideWish(
  wish,
  { approve, actorId, on, note, today, spending = SPENDING_DEFAULTS, wishes = [], waiver } = {},
) {
  assertStatus(wish, 'waiting');
  const decidedOn = on ? toDateOnly(on) : null;

  if (approve && !isReady(wish, today ?? on)) {
    throw new Error(`wish ${wish.id} is still waiting until ${wish.readyOn}`);
  }

  // A cap waiver lives on its own field rather than being folded into
  // `decisionNote`, so a screen can render "over the limit, allowed because …"
  // as the exception it is without having to parse a free-text note, and so a
  // parent's ordinary note is never mistaken for a justification.
  let capWaiverReason = wish.capWaiverReason ?? null;

  if (approve && (wish.kind ?? null) === 'toy') {
    const spend = withSpendingDefaults(spending);
    // The cap is a calendar-month rule, so it is scoped to the month the
    // decision lands in. With no date there is no month, and a back-dated
    // correction with neither `on` nor `today` is not a purchase happening now.
    const month = decidedOn ?? (today ? toDateOnly(today) : null);

    if (spend.toyCapCents !== null && month) {
      const status = toyCapStatus(wishes, {
        childId: wish.childId,
        month,
        spending: spend,
        wish,
      });

      if (status.overCap) {
        // Same loudness rule as the cooldown waiver: a parent may always go
        // over, and going over silently is what makes the limit stop meaning
        // anything.
        if (!waiver) {
          throw new Error(
            `wish ${wish.id} is over the toy limit for ${monthKey(month)}: ` +
              `${status.spentCents} + ${wish.amountCents} of ${status.capCents} cents`,
          );
        }
        capWaiverReason = waiver;
      }
    }
  }

  return {
    ...wish,
    status: approve ? 'bought' : 'declined',
    decidedBy: actorId ?? null,
    decidedOn,
    decisionNote: note ?? null,
    capWaiverReason,
  };
}

/**
 * The settlement that pays for a bought wish.
 *
 * Money leaving the ledger because the thing was actually bought — the half of
 * the loop `ledger.js` exists to keep honest. `owedCents` and `paidCents` are
 * both the wish price, so there is never a remainder: a wish is settled by
 * buying the named thing, not by counting coins onto a counter, and a final
 * price that differs from the listed one is a different event (correct the wish
 * before deciding, or write a reversal) rather than a partial payment.
 *
 * Requires `status === 'bought'`. Approving is what spends (CLAUDE.md), so a
 * settlement for a wish nobody approved is money moving with no decision behind
 * it — the one thing this whole module is here to prevent.
 */
export function purchaseEntry({ wish, actorId, on } = {}) {
  assertStatus(wish, 'bought');
  if (!wish.childId) throw new Error('a wish purchase needs a childId');
  if (!wish.id) throw new Error('a wish purchase needs a stable wish id');
  if (!Number.isInteger(wish.amountCents) || wish.amountCents <= 0) {
    throw new Error(`a wish purchase must be a positive whole number of cents; got ${wish.amountCents}`);
  }

  return settlementEntry({
    childId: wish.childId,
    // A wish against the Give bucket is a donation, not a toy. Defaulting to
    // spend keeps a wish written before buckets existed from settling out of
    // the wrong pot.
    bucket: wish.bucket ?? 'spend',
    owedCents: wish.amountCents,
    paidCents: wish.amountCents,
    method: 'purchased',
    actorId,
    note: wish.title,
    requestId: wish.id,
    occurredAt: on ? toDateOnly(on) : null,
  }).entry;
}

/**
 * The stat that does the teaching: what waiting actually did for this child.
 *
 * Two rules make the number honest.
 *
 * **Only wishes that entered the room count.** A wish still sitting on the list
 * was never waited on, and a wish crossed off the list without ever being asked
 * for was not restraint under pressure — it was a change of mind about
 * something that cost nothing to want. Counting either would inflate the
 * child's own evidence about themselves with things they never actually did,
 * and an inflated stat is worse than no stat: it teaches nothing and it stops
 * being believed the first time a child notices. `askedOn` is the marker,
 * because `askForWish` is the only door in.
 *
 * **`keptCents` counts `let_go` only, never `declined`.** Money a parent
 * refused is not money the child chose not to spend. Folding a run of parental
 * noes into "money you kept" would hand the child credit for someone else's
 * decision, which is flattery, and flattery is not evidence.
 *
 * `stillWaiting` is reported so a screen can account for the gap: `waited` is
 * everything that ever entered the room, and the three outcomes need not sum to
 * it while wishes are still in flight.
 */
export function waitOutcomes(wishes = []) {
  const out = { waited: 0, stillWanted: 0, letGo: 0, declined: 0, stillWaiting: 0, keptCents: 0 };

  for (const wish of wishes) {
    // `saving` is on the list, not in the room. A child putting money aside for
    // something has not yet sat out a cooldown on it, and counting it as a wait
    // they saw through would credit them for a decision they have not made
    // yet. The status check is belt and braces next to `askedOn`: a wish that
    // stopped saving and was asked for later carries both, and only the ask
    // counts.
    if (!wish?.askedOn) continue;
    if (wish.status === 'wished' || wish.status === 'saving') continue;
    out.waited += 1;
    if (wish.status === 'bought') out.stillWanted += 1;
    else if (wish.status === 'declined') out.declined += 1;
    else if (wish.status === 'waiting') out.stillWaiting += 1;
    else if (wish.status === 'let_go') {
      out.letGo += 1;
      out.keptCents += Math.trunc(Number(wish.amountCents) || 0);
    }
  }

  return out;
}

/**
 * The price in the child's own units: "about 3 weeks of allowance".
 *
 * CODING_PLAN §15, "price things in their units". Dollars are the parent's unit
 * and mean very little to a child; weeks of their own income mean something
 * immediately, and it is the honest comparison because it is what the purchase
 * actually costs them.
 *
 * Rounds up, and never down to nothing: a $2 thing on a $10 allowance is a week
 * of waiting if you have $0, and rendering it as "0 weeks" would make the
 * cheapest purchases look free. `null` when there is no income to divide by —
 * a child with no allowance has no answer, and the screen must say nothing
 * rather than divide by zero and print `Infinity weeks`.
 */
export function priceInPeriods(amountCents, perPeriodCents) {
  const per = Number(perPeriodCents) || 0;
  if (per <= 0) return null;
  const amount = Number(amountCents) || 0;
  if (amount <= 0) return 0;
  return Math.max(1, Math.ceil(amount / per));
}

/* ---------------------------------------------------------------------------
 * Saving for it
 * ------------------------------------------------------------------------ */

/**
 * "I'm saving for that" — the state between wanting a thing and asking for it.
 *
 * This is the state the waiting room was missing. A cooldown answers "not
 * yet"; it does not answer "with what?". A child who has named a thing, seen
 * its price against their own Save balance, and decided to point their money at
 * it is doing the thing this whole app is for, and until now the list had
 * nowhere to record it — the wish sat in `wished` looking identical to a
 * passing thought about a keychain.
 *
 * The bucket moves to `save` because that is where the money for it has to
 * accumulate and where `purchaseEntry` must eventually settle it from. A wish
 * bought out of Spend after a month of saving would leave the saved money
 * sitting there and the child unable to see what their saving bought.
 *
 * The price is fixed here in the sense that it is already on the wish and this
 * transition does not touch it. Progress is measured against a balance passed
 * in at read time (`savingProgress`) and is never stored: a stored "saved so
 * far" is a second counter for money the ledger already knows about, and the
 * two drift the first time anything else spends out of Save.
 */
export function startSaving(wish, { on } = {}) {
  assertStatus(wish, 'wished');
  return {
    ...wish,
    status: 'saving',
    bucket: 'save',
    savingSince: on ? toDateOnly(on) : null,
    savingStoppedOn: null,
  };
}

/**
 * Changing their mind about saving for it, without giving up on wanting it.
 *
 * Back to `wished` and back to the Spend bucket. Deliberately not `let_go`:
 * deciding a thing is not worth saving up for is a different event from
 * deciding you no longer want it, and only the second one is the restraint
 * `waitOutcomes` reports. Stopping costs nothing and nothing about it is
 * recorded as a failure.
 */
export function stopSaving(wish, { on } = {}) {
  assertStatus(wish, 'saving');
  return {
    ...wish,
    status: 'wished',
    bucket: 'spend',
    savingSince: null,
    savingStoppedOn: on ? toDateOnly(on) : null,
  };
}

/**
 * How far along the saving is, computed fresh every time.
 *
 * `saveBalanceCents` comes from the ledger projection, so this is a pure view
 * over a number that already exists. Nothing here is a second source of truth
 * about a balance (CLAUDE.md: balances are projections, never a stored
 * counter).
 *
 * `pct` is floored and computed in integer cents, for the same reason
 * `displayGold` floors: showing 100% for $19.99 of a $20 target and then not
 * letting the child buy it is the small betrayal that ends trust in the number.
 * It only reads 100 when `reached` is true.
 */
export function savingProgress({ wish, saveBalanceCents } = {}) {
  const targetCents = Math.trunc(Number(wish?.amountCents) || 0);
  const haveCents = Math.trunc(Number(saveBalanceCents) || 0);

  if (targetCents <= 0) {
    return { targetCents, haveCents, remainingCents: 0, pct: 100, reached: true };
  }

  const reached = haveCents >= targetCents;
  const raw = Math.floor((haveCents * 100) / targetCents);

  return {
    targetCents,
    haveCents,
    remainingCents: Math.max(0, targetCents - haveCents),
    pct: Math.max(0, Math.min(100, raw)),
    reached,
  };
}

/* ---------------------------------------------------------------------------
 * The toy day and the toy limit
 * ------------------------------------------------------------------------ */

/** `YYYY-MM` from a date, or from a month string, so either can be passed in. */
export function monthKey(date) {
  return toDateOnly(date).slice(0, 7);
}

/**
 * The next shopping trip: the first date on or after `fromDate` whose
 * day-of-month is `toyDay`.
 *
 * On the day itself the answer is today, not next month — the same `>=`
 * boundary as `isReady`, and for the same reason: a toy day a child cannot
 * shop on is a toy day wearing the wrong label. Rolls over month and year ends
 * by construction, and `toyDay` is 1..28 so the target day exists in every
 * month. Null when there is no toy day configured, which is how callers ask
 * "is this feature even on".
 */
export function nextToyDay(fromDate, toyDay) {
  const day = normaliseToyDay(toyDay);
  if (day === null) return null;

  const { year, month, day: from } = parseDateOnly(fromDate);
  let y = year;
  let m = month;
  if (from > day) {
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }

  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * What this child has already spent on toys this month.
 *
 * Four filters, and each one is the difference between a limit and a
 * grievance:
 *
 * - **`bought` only.** A wish still waiting has not spent anything, and one let
 *   go never will. Counting either would let a child use up their limit by
 *   wanting things, which punishes exactly the behaviour the waiting room is
 *   trying to encourage — putting it on the list instead of buying it.
 * - **`declined` never counts.** Same rule as `keptCents`: a parent's no is not
 *   the child's spending.
 * - **This child only.** One shared pot is `envelope.js`; this is a child's own
 *   money and a sibling's toy cannot eat it.
 * - **`decidedOn`, not `askedOn`.** The month a purchase is approved is the
 *   month it costs. A wish asked for on the 30th and approved on the 2nd spends
 *   in the new month, which is also the only reading under which a catch-up
 *   decision after a long wait cannot retroactively blow a month that is
 *   already closed.
 */
export function toySpentInMonth(wishes = [], { childId, month } = {}) {
  const key = month ? monthKey(month) : null;
  let cents = 0;

  for (const wish of wishes) {
    if (!wish || wish.status !== 'bought') continue;
    if ((wish.kind ?? null) !== 'toy') continue;
    if (childId != null && wish.childId !== childId) continue;
    if (!wish.decidedOn) continue;
    if (key && monthKey(wish.decidedOn) !== key) continue;
    cents += Math.trunc(Number(wish.amountCents) || 0);
  }

  return cents;
}

/**
 * Where this wish would leave the month's toy limit — the number the screen
 * shows and the number `decideWish` enforces, computed once, here.
 *
 * `remainingCents` is not clamped at zero. Same call as the Fuel gauge: a
 * limit that has been passed should say so, and silently flooring it at zero
 * would hide the one state a parent needs to see. When there is no cap it is
 * null rather than Infinity, so a screen can tell "no limit" from "loads left".
 *
 * The wish being priced is excluded from `spentCents` by id, so calling this on
 * a wish that has already been bought — a re-render, a retried request —
 * reports the month it is in rather than counting it twice.
 *
 * A non-toy wish is never over the cap: the cap is scoped to `toy` and a bike
 * is not a toy no matter what it costs. The month's totals still come back, so
 * a screen can show the toy gauge next to any wish.
 */
export function toyCapStatus(wishes = [], { childId, month, spending = SPENDING_DEFAULTS, wish } = {}) {
  const spend = withSpendingDefaults(spending);
  const capCents = spend.toyCapCents;
  const child = childId ?? wish?.childId ?? null;
  const key = month ? monthKey(month) : wish?.decidedOn ? monthKey(wish.decidedOn) : null;

  const others = wish?.id ? wishes.filter((w) => w?.id !== wish.id) : wishes;
  const spentCents = key ? toySpentInMonth(others, { childId: child, month: key }) : 0;

  const amount = (wish?.kind ?? null) === 'toy' ? Math.trunc(Number(wish?.amountCents) || 0) : 0;

  return {
    capCents,
    spentCents,
    remainingCents: capCents === null ? null : capCents - spentCents,
    overCap: capCents !== null && spentCents + amount > capCents,
  };
}
